import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

export const EVENT_DETAIL_DIRECTORY_PAGE_SIZE = 12

export const useDirectoryPagination = <T>(items: MaybeRefOrGetter<readonly T[]>, pageSize: number = EVENT_DETAIL_DIRECTORY_PAGE_SIZE) => {
  const pageIndex = ref(0)

  const total = computed(() => toValue(items).length)
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize)))

  watch(
    () => toValue(items),
    () => {
      pageIndex.value = 0
    }
  )

  watch(pageCount, count => {
    if (pageIndex.value > count - 1) {
      pageIndex.value = Math.max(0, count - 1)
    }
  })

  const pageItems = computed(() => {
    const list = toValue(items)
    const start = pageIndex.value * pageSize
    return list.slice(start, start + pageSize)
  })

  const rangeStart = computed(() => (total.value === 0 ? 0 : pageIndex.value * pageSize + 1))
  const rangeEnd = computed(() => Math.min(total.value, (pageIndex.value + 1) * pageSize))

  const canGoPrevious = computed(() => pageIndex.value > 0)
  const canGoNext = computed(() => pageIndex.value < pageCount.value - 1)

  const goPrevious = () => {
    if (canGoPrevious.value) pageIndex.value -= 1
  }

  const goNext = () => {
    if (canGoNext.value) pageIndex.value += 1
  }

  return {
    pageIndex,
    pageItems,
    total,
    rangeStart,
    rangeEnd,
    canGoPrevious,
    canGoNext,
    goPrevious,
    goNext,
  }
}
